import React, {useState} from 'react';

import './Patients.scss';
import { useQuery } from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';

import { Subject } from 'rxjs';
import ButtonIcon from '../forms/buttons/Button.icon';
import PatientBasePatient from './Patients.patient';
import { Link } from 'react-router-dom';
import DropdownText from '../forms/dropdown/dropdown.text';
import cacheStorage from '../services/cacheStorage';
import SpinnerLoader from "../visuals/SpinnerLoader";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../services/interactions/page.view";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";


function PatientsBase({ currentOrg, toaster, currentUser }) {
    const {t} = useTranslation(['participants','users','common']);
    const [data, setData] = useState({ getUserPatients: [] });
    const [loadData, setLoadData] = useState(true);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');
    const [newsOnTop, setNewsOnTop] = useState(true);

    const [programOptions, setProgramOptions] = useState([]);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');

    const [programIdFilter, setProgramIdFilter] = useState(null);

    const [searchTerm] = useState(new Subject());

    useQuery(gqlSchemaLibrary.patients.getPersonByOrganization, {
        onCompleted: (onPatients) => {
            const patientsByOrg = onPatients.getPersonByOrganization.map(p => ({ ...p }));
            console.log('[Patient List] User Patients: ', patientsByOrg);
            if (patientsByOrg) {
                const lastPatientsSeenString = cacheStorage.getItem(`${currentUser.selectedOrgId}_patientlist`);
                if (lastPatientsSeenString && lastPatientsSeenString.value) {
                    const lastPatientsSeenId = JSON.parse(lastPatientsSeenString.value);
                    patientsByOrg.forEach((patient) => patient.isNew = !lastPatientsSeenId.some(id => id === patient._id));
                    // Set for program assign to show as "new", but avoid overwriting 
                    const newForPrograms = cacheStorage.getItem(`${currentUser.selectedOrgId}_programPatients`);
                    if (!!newForPrograms && newForPrograms.value) {
                        const allNewForPrograms = JSON.parse(newForPrograms.value).concat(patientsByOrg.filter(p => p.isNew).map(p => p._id));
                        cacheStorage.saveItem(`${currentUser.selectedOrgId}_programPatients`, JSON.stringify(allNewForPrograms));
                    } else {
                        cacheStorage.saveItem(`${currentUser.selectedOrgId}_programPatients`, JSON.stringify(patientsByOrg.filter(p => p.isNew).map(p => p._id)));
                    }
                }
                setData({ getUserPatients: patientsByOrg });
                onSelect(patientsByOrg[0] || null);
                cacheStorage.saveItem(`${currentUser.selectedOrgId}_patientlist`, JSON.stringify(patientsByOrg.map(p => p._id)));
            }
            setLoadData(false);
        },
        fetchPolicy: 'cache-and-network'
    });

    const onSelect = (patient) => {
        console.log('[PatientList.List] selecting: ', patient);
        setSelectedPatient(patient);
    }

    const onUpdatePatient = (patient) => {
        if(!patient){
            return;
        }
        const patients = data.getUserPatients;
        const toEditIndex = patients.findIndex(p => p._id === patient._id);
        patients[toEditIndex] = patient;
        if(toEditIndex > -1) {
            setData({ getUserPatients: patients });
        }    
    }


    useQuery(gqlSchemaLibrary.programs.query.getCurrentOrgPrograms, {
        onCompleted: (onPrograms) => {
            if (onPrograms.getCurrentOrgPrograms) {
                setProgramOptions(onPrograms.getCurrentOrgPrograms);
            }
        },
        fetchPolicy: 'cache-and-network'
    })

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tag: 'input', event: 'onSearch', action: 'input search patients', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const openProgramFilter = (program) => {
        setProgramIdFilter(program.value);
    }

    const dropdownOptions = [{ label: t('all-programs'), value: null }].concat(programOptions.map((o) => ({ label: o.name, value: o._id })));

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
            setNewsOnTop(false);
        }
    }

    const filteredPatients = data.getUserPatients
        .filter(p => !!programIdFilter ? p.programId === programIdFilter : true)
        .filter(p => {
            if (!search) {
                return true;
            }
            const firstName = (p.user.bio.firstname || '').toLowerCase();
            const lastName = (p.user.bio.lastname || '').toLowerCase();
            const searchWords = search.split(' ').map(w => w.toLowerCase());
            return searchWords.reduce((p, word) => {
                return p && (firstName.includes(word) || lastName.includes(word));
            }, true);
        })
        .sort( (a, b) => {
            if(!sorting){
                return true;
            }
            if(sorting === 'name'){
                const aName = (a.user.bio.lastname||'')+' '+(a.user.bio.firstname||'');
                const bName = (b.user.bio.lastname||'')+' '+(b.user.bio.firstname||'');
                if(sortAscending){
                    return aName.localeCompare(bName);
                }else{
                    return bName.localeCompare(aName);
                }
            }else{
                if(sortAscending){
                    return (a.dateOfBirth > b.dateOfBirth)? 1: -1;
                }else{
                    return (a.dateOfBirth > b.dateOfBirth)? -1: 1;
                }
            }
        })
    const oldFilteredPatients = filteredPatients.filter(p => {
        if(newsOnTop){
            return p && !p.isNew;
        }
        return true;
    });
    const newFilteredPatients = filteredPatients.filter(p => {
        if(newsOnTop){
            return p && p.isNew;
        }
        return false;
    });

    const countParticipants = oldFilteredPatients.length+newFilteredPatients.length;

    return (
        <div id="PatientsBase">
            <div className="filter">
                <DropdownText
                    transparent={true}
                    onChange={openProgramFilter}
                    options={dropdownOptions}
                    label={t('filter-by-program').toUpperCase()}
                    placeholder={t('filter-by-program')}
                    value={programIdFilter}
                    event={PAGES.PARTICIPANTS_LIST}
                />
                <div className={`search ${searchFocused ? 'focused' : ''}`}>
                    <input value={search} placeholder={t('common:search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                    <i className="material-icons">
                        search
                    </i>
                </div>
                <div className="addAction">
                    <p className="_counter">
                        {countParticipants} participant{countParticipants === 1? '' : 's'}
                    </p>
                    {
                        !currentUser.userRoles.userPatients.create ?
                            ''
                            :
                            <Link to='/participants/create'>
                                <div className="enrollAction">
                                    <ButtonIcon>
                                        <span className="material-icons">
                                            add
                                </span>
                                    </ButtonIcon>
                                    <p>{t('common:new')}</p>
                                </div>
                            </Link>
                    }
                </div>
            </div>
            <div className="patientsList scrollBar">
                <div className="headers">
                    <div></div>
                    <div className="selectable sortable">
                        <p onClick={sortList('name')}>
                            {t('users:name')}
                        </p>
                        <div className="_updown" onClick={sortList('name')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable sortable centered">
                        <p onClick={sortList('age')}>
                            {t('users:age')}
                        </p>
                        <div className="_updown" onClick={sortList('age')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:gender')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:date-of-birth')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:telephone')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:program')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('users:status')}
                        </p>
                        <div className="_updown"></div>
                    </div>
                    <div></div>
                </div>
                {
                    newFilteredPatients.map((patient) => (
                        <PatientBasePatient key={patient._id} currentOrg={currentOrg} currentUser={currentUser} programOptions={programOptions} patient={patient} selected={selectedPatient && patient._id === selectedPatient._id} onSelected={setSelectedPatient} onUpdatePatient={onUpdatePatient}/>
                    ))
                }
                {
                    oldFilteredPatients.map((patient) => (
                        <PatientBasePatient key={patient._id} currentOrg={currentOrg} currentUser={currentUser} programOptions={programOptions} patient={patient} selected={selectedPatient && patient._id === selectedPatient._id} onSelected={setSelectedPatient} onUpdatePatient={onUpdatePatient}/>
                    ))
                }
                { loadData? <SpinnerLoader/>: '' }
            </div>
        </div>
    )
}

export default PageViewLog(PatientsBase);
